import { z } from "zod";
import { useQuery, useMutation } from "react-query";
import { useMemo } from "react";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { zodParseWithLog } from "src/api/apiErrorLogs";

/**
 * Enum for JSON attribute types.
 */
export enum JsonAttributeType {
    STRING = "string",
    INTEGER = "int",
    FLOAT = "float",
    BOOLEAN = "bool",
}

/**
 * Enum for aggregation types.
 */
export enum AggregationType {
    NONE = "none",
    SUM = "sum",
    AVERAGE = "average",
}

/**
 * Fetches all JSON attributes from the API.
 * Defaults to ordering by label in ascending order.
 */
export const useGetAllJsonAttributes = () => {
    const url = "/api/entity_json_attribute_definitions?order[label]=asc";

    const query = useQuery({
        queryKey: "getAllJsonAttributes",
        cacheTime: 15 * 60 * 1000, // 15 minutes
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        queryFn: async () => {
            return await loggedInApiCall.get(url);
        },
        select: (response) => {
            const attributes = response.data["hydra:member"];
            return zodParseWithLog(attributes, JsonAttributeSchema.array(), "getAllJsonAttributes");
        },
    });

    // Stabilize the empty array reference to prevent unnecessary re-renders
    const jsonAttributes = useMemo(() => {
        return query.data ?? [];
    }, [query.data]);

    return {
        jsonAttributes,
        ...query,
    };
};

/**
 * A mutation hook for creating a new JSON attribute (POST) or updating an existing one (PUT).
 */
export const useCreateOrUpdateJsonAttribute = () => {
    return useMutation({
        mutationFn: async (attribute: JsonAttributeWrite) => {
            return attribute?.id === undefined ?
                    await loggedInApiCall.post("/api/entity_json_attribute_definitions", attribute)
                :   await loggedInApiCall.put(`/api/entity_json_attribute_definitions/${attribute.id}`, attribute);
        },
    });
};

/**
 * A mutation hook for deleting a JSON attribute by its ID.
 */
export const useDeleteJsonAttribute = () => {
    return useMutation({
        mutationFn: async (id: number) => {
            return await loggedInApiCall.delete(`/api/entity_json_attribute_definitions/${id}`);
        },
    });
};

/**
 * A mutation hook for checking if a JSON attribute is in use.
 */
export const useCheckJsonAttributeUsage = () => {
    return useMutation({
        mutationFn: async (id: number) => {
            return await loggedInApiCall.get(`/api/entity_json_attribute_definitions/${id}/check-usage`);
        },
    });
};

export type JsonAttribute = {
    "@id"?: string;
    id: number;
    label: string;
    identifier: string;
    entityClass: string;
    type: JsonAttributeType;
    aggregationType?: AggregationType | null;
    canView?: boolean;
    canModify?: boolean;
    canDelete?: boolean;
};

export const JsonAttributeSchema: z.ZodSchema<JsonAttribute> = z.object({
    "@id": z.string().optional(),
    id: z.number(),
    label: z.string(),
    identifier: z.string(),
    entityClass: z.string(),
    type: z.nativeEnum(JsonAttributeType),
    aggregationType: z.nativeEnum(AggregationType).nullable().optional(),
    canView: z.boolean().optional(),
    canModify: z.boolean().optional(),
    canDelete: z.boolean().optional(),
});

export type JsonAttributeWrite = {
    id?: number;
    label: string;
    identifier: string;
    entityClass: string;
    type: JsonAttributeType;
    aggregationType?: AggregationType | null;
};

export const JsonAttributeWriteSchema: z.ZodSchema<JsonAttributeWrite> = z.object({
    id: z.number().optional(),
    label: z.string(),
    identifier: z.string(),
    entityClass: z.string(),
    type: z.nativeEnum(JsonAttributeType),
    aggregationType: z.nativeEnum(AggregationType).nullable().optional(),
});

const getZodTypeFromAttributeType = (type: JsonAttributeType) => {
    switch (type) {
        case JsonAttributeType.STRING:
            return z.string().optional().nullable();
        case JsonAttributeType.INTEGER:
            return z.number().optional().nullable();
        case JsonAttributeType.FLOAT:
            return z.number().optional().nullable();
        case JsonAttributeType.BOOLEAN:
            return z.boolean().optional().nullable();
        default:
            return z.any();
    }
};

/**
 * @param attributes The list of attributes to build a schema from.
 * @returns A Zod schema representing the dynamic properties.
 */
export const createDynamicAttributesSchema = (attributes: JsonAttribute[] | undefined) => {
    if (!attributes) {
        return z.object({});
    }

    const shape: Record<string, z.ZodTypeAny> = {};
    for (const attribute of attributes) {
        shape[attribute.identifier] = getZodTypeFromAttributeType(attribute.type);
    }

    return z.object(shape);
};
